import { getNotificationCategoriesWithNotifications } from 'app/pages/notifications/const';
import { Field, FieldProps } from 'formik';
import { CheckboxInput } from 'lib/components';
import { useAuth } from 'lib/context';
import * as React from 'react';
import styled from 'styled-components/macro';
import { Container, FieldLabel, Row, SectionTitle } from './common/components';
import { HiChevronRight, HiChevronDown } from 'react-icons/hi';
import { UserValues } from '../userValidator';
import { useCDK } from 'lib/hooks/useCdk';
import { useTranslation } from 'react-i18next';

interface FormProps {
  isSubmitting?: boolean;
  setFieldValue?: (field: string, value: boolean) => void;
  leadNotificationPreferences?: Record<
    string,
    Record<string, Array<{ name: string; value: boolean }>>
  >;
  values?: UserValues;
  showLeadsNotifPref?: boolean;
}

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const NotificationLabel = styled.div`
  display: flex;
  width: 260px;
`;

const ActionsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 112px;
  align-items: center;
`;

const MarginTop = styled.div`
  margin-top: 20px;
`;

const ExpandableLabel = styled(FieldLabel)`
  cursor: pointer;
  font-weight: 600;
  user-select: none;
  min-width: 150px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CheckboxInputField = (
  fieldName: string,
  isSubmitting: boolean = false,
  setFieldValue?: (field: string, value: boolean) => void,
  values?: UserValues
) => {
  return (
    <Field type='checkbox' name={fieldName}>
      {({ field }: FieldProps) => {
        const { name, value, ...rest } = field;
        return (
          <CheckboxInput
            {...rest}
            name={name}
            checked={value ?? values?.[name] ?? false}
            disabled={isSubmitting}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue?.(name, e.target.checked);
            }}
          />
        );
      }}
    </Field>
  );
};

const prettifyKey = (key: string): string => {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^./, str => str.toUpperCase());
};

// Handles both initial flattened Formik values and nested updated values from state changes
const getLeadToggleValue = (
  values: UserValues | undefined,
  key: 'Lead Assignment' | 'Lead Reassignment'
): boolean => {
  return (
    values?.leadNotificationPreferences?.leadOpportunities?.leads?.[key] ??
    values?.[`leadNotificationPreferences.leadOpportunities.leads.${key}`] ??
    false
  );
};

export const NotificationPreferencesComponent = ({
  isSubmitting,
  setFieldValue,
  leadNotificationPreferences,
  values,
  showLeadsNotifPref,
}: FormProps) => {
  const { isCdkUser } = useCDK();
  const { userData } = useAuth();
  const { t } = useTranslation();
  const [allLeadTypesToggleOverride, setAllLeadTypesToggleOverride] =
    React.useState<boolean | null>(null);
  const [areLeadsDisabled, setAreLeadsDisabled] =
    React.useState<boolean>(false);

  React.useEffect(() => {
    const leadAssignment = getLeadToggleValue(values, 'Lead Assignment');
    const leadReassignment = getLeadToggleValue(values, 'Lead Reassignment');

    setAreLeadsDisabled(!leadAssignment && !leadReassignment);
  }, [values]);

  const notifcationCategories = React.useMemo(
    () => getNotificationCategoriesWithNotifications(userData, isCdkUser, t),
    []
  );

  const leadTypesFields = React.useMemo(() => {
    return Object.keys(values || {}).filter(key =>
      key.startsWith('leadNotificationPreferences.leadOpportunities.leadTypes.')
    );
  }, [values]);

  const allLeadTypesSelected =
    leadTypesFields.length > 0 &&
    leadTypesFields.every(fieldName => values?.[fieldName]);

  const allLeadTypesToggleValue =
    allLeadTypesToggleOverride !== null
      ? allLeadTypesToggleOverride
      : allLeadTypesSelected;

  const leadNotificationCategories = React.useMemo(() => {
    if (!showLeadsNotifPref) return [];

    if (!leadNotificationPreferences) return [];

    const categories: Array<{
      categoryName: string;
      isLead: boolean;
      notifications: Array<{
        id: string;
        name: string;
        valueInApp: string;
        valueEmail: false;
        isLead?: boolean;
      }>;
    }> = [];

    Object.entries(leadNotificationPreferences).forEach(
      ([preferenceType, preferenceValues]) => {
        Object.entries(preferenceValues).forEach(([key, values]) => {
          categories.push({
            categoryName: prettifyKey(key),
            isLead: true,
            notifications: values.map(entry => ({
              id: `${preferenceType}.${key}.${entry.name}`,
              name: entry.name,
              valueInApp: `leadNotificationPreferences.${preferenceType}.${key}.${entry.name}`,
              valueEmail: false,
              isLead: true,
            })),
          });
        });
      }
    );

    return categories;
  }, [leadNotificationPreferences]);

  const [openCategories, setOpenCategories] = React.useState<
    Record<string, boolean>
  >(() => {
    const initial: Record<string, boolean> = {};
    notifcationCategories.forEach(cat => {
      initial[cat.categoryName] = false;
    });
    return initial;
  });

  const toggleCategory = (categoryName: string) => {
    setOpenCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName],
    }));
  };

  React.useEffect(() => {
    if (allLeadTypesToggleOverride !== null && leadTypesFields.length > 0) {
      const allMatch = leadTypesFields.every(
        fieldName => values?.[fieldName] === allLeadTypesToggleOverride
      );
      if (allMatch) {
        setAllLeadTypesToggleOverride(null);
      }
    }
  }, [values, leadTypesFields, allLeadTypesToggleOverride]);

  return (
    <MarginTop>
      <Container>
        <SectionTitle>{t('Send Notifications')}</SectionTitle>
      </Container>

      <Container>
        <SectionTitle />
        <Column style={{ flexDirection: 'column', width: '100%' }}>
          {[...notifcationCategories, ...leadNotificationCategories].map(
            (category, index) => {
              const notifications = category.notifications;
              const isOpen = openCategories[category.categoryName];
              const isLead = category.isLead;

              return (
                <Row
                  key={index}
                  style={{
                    borderBottom: '1px solid #EEEFF2',
                    flexDirection: 'column',
                  }}
                >
                  <ExpandableLabel
                    onClick={() => toggleCategory(category.categoryName)}
                  >
                    {isOpen ? <HiChevronDown /> : <HiChevronRight />}{' '}
                    {category.categoryName}
                  </ExpandableLabel>

                  {isOpen && (
                    <div style={{ marginLeft: 120 }}>
                      <Row style={{ marginTop: 4 }}>
                        <NotificationLabel style={{ width: 260 }} />
                        <ActionsWrapper>
                          {!isLead && <div>{t('Email')}</div>}
                          <div
                            style={{
                              marginLeft: !isLead ? 42 : 0,
                            }}
                          >
                            {t('Mobile')}
                          </div>
                        </ActionsWrapper>
                      </Row>

                      {category.categoryName === 'Lead Types' && (
                        <Row style={{ alignItems: 'center' }}>
                          <NotificationLabel>
                            {t('All Lead Types')}
                          </NotificationLabel>
                          <ActionsWrapper style={{ marginLeft: 11 }}>
                            <CheckboxInput
                              name='allLeadTypesToggle'
                              checked={allLeadTypesToggleValue}
                              disabled={isSubmitting || areLeadsDisabled}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                const isChecked = e.target.checked;
                                setAllLeadTypesToggleOverride(isChecked);
                                leadTypesFields.forEach(fieldName =>
                                  setFieldValue?.(fieldName, isChecked)
                                );
                              }}
                            />
                          </ActionsWrapper>
                        </Row>
                      )}

                      {notifications.map((notification, i) => {
                        const emailFieldName = `notificationPreferences.${notification.valueEmail}`;
                        const mobileFieldName = isLead
                          ? notification.valueInApp
                          : `notificationPreferences.${notification.valueInApp}`;

                        const isLeadTypeField =
                          isLead &&
                          notification.valueInApp.includes(
                            'leadNotificationPreferences.leadOpportunities.leadTypes'
                          );

                        const shouldDisable =
                          isLeadTypeField && areLeadsDisabled;

                        return (
                          <Row key={i} style={{ alignItems: 'center' }}>
                            <NotificationLabel
                              style={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {notification.name}
                            </NotificationLabel>
                            <ActionsWrapper style={{ marginLeft: 11 }}>
                              {!isLead &&
                                CheckboxInputField(
                                  emailFieldName,
                                  isSubmitting,
                                  setFieldValue,
                                  values
                                )}
                              {CheckboxInputField(
                                mobileFieldName,
                                isSubmitting || shouldDisable,
                                setFieldValue,
                                values
                              )}
                            </ActionsWrapper>
                          </Row>
                        );
                      })}
                    </div>
                  )}
                </Row>
              );
            }
          )}
        </Column>
      </Container>
    </MarginTop>
  );
};
